// deployment_class: customer_env_allowed
/**
 * Bring documents into a project.
 *
 * The scaffolding is built when the project is created; this is what fills it.
 * Everything uploaded here belongs to THIS project — attribution comes from the
 * project you are standing in, never from guessing at the document.
 *
 * ── What the customer is told ──────────────────────────────────────────────
 * Not "OK". They get what we think the document is, HOW SURE we are, the
 * evidence behind it, how many rows landed, and — critically — any sheet we
 * could not read. An import that quietly reads half a workbook and reports
 * success is the failure this whole path exists to prevent.
 *
 * The classification is a PROPOSAL. Confirming or correcting it is the next
 * step, and the correction is what teaches the classifier.
 *
 * ── The five signals (E3 2026-08-24, from the drag-and-drop reference) ─────
 * 1. DRAG FEEDBACK — the zone answers back before the drop: border, glow, copy.
 *    "Users hesitate when nothing moves."
 * 2. HONEST PROGRESS — percent and time left, never a spinner. A spinner cannot
 *    be checked and looks the same whether the work is nearly done or wedged.
 * 3. INLINE RETRY — the file stays in memory, progress is preserved, and one tap
 *    resumes. "Never make them start over."
 * 4. UPLOAD PREVIEW — type, size and what we actually read out of it. A filename
 *    is not feedback.
 * 5. INDEPENDENT QUEUE — every file its own lane, its own progress, its own
 *    retry. "One failure never blocks the others."
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { apiUpload } from '@v3-shell/api/api-client-base';
import {
  ACCEPT_ATTRIBUTE,
  entriesFromDrop,
  filesFromEntries,
  triage,
  type FolderScan,
} from './folder-import';

export interface ImportOutcome {
  document_id: string;
  proposed_doc_type: string;
  confidence: number;
  evidence: Array<{ surface?: string; phrase?: string }>;
  records_written: number;
  record_type: string;
  landing: 'typed' | 'generic';
  unread_sheets: string[];
  truncated: boolean;
  /**
   * The verdict, from `sat_import_receipt`. `nothing_landed` is the one that
   * must never be rendered as a quiet success: the panel used to show only
   * "Read as Strategic plan" for a document whose contents never became
   * records, because the rows line is suppressed at zero.
   */
  outcome?: 'complete' | 'partial' | 'nothing_landed';
  /** Why, in words. Null only when `complete`. */
  outcome_reason?: string | null;
}

function humanise(docType: string): string {
  return docType.replace(/-/g, ' ').replace(/^./, (c) => c.toUpperCase());
}

/** `2.4 MB` — the size the customer sees, in the unit they think in. */
function humanSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** `2m 05s left` — an estimate is only useful if it reads as time. */
function humanTimeLeft(seconds: number): string {
  if (seconds < 60) return `${seconds}s left`;
  const minutes = Math.floor(seconds / 60);
  return `${minutes}m ${String(seconds % 60).padStart(2, '0')}s left`;
}

/** The badge on a row — what kind of thing this is, at a glance. */
export function fileKind(name: string): string {
  const dot = name.lastIndexOf('.');
  return dot < 0 ? 'FILE' : name.slice(dot + 1).toUpperCase();
}

/** One file's journey, so a batch reports per-document rather than in aggregate. */
export interface QueueItem {
  /**
   * The File itself, HELD. Retry must not send the customer back to the file
   * dialog — "never make them start over" is only true if we still have it.
   */
  file: File;
  name: string;
  size: number;
  kind: string;
  /**
   * `stopped` is deliberately NOT `failed`. Nothing went wrong with the
   * document — a person chose to walk away, and reporting that as a failure
   * would blame the file for a decision the user made.
   */
  /**
   * `held` = accepted and waiting on a project, not failed. The distinction
   * matters on screen: a failure invites a retry, a hold invites naming the
   * project the file belongs to.
   */
  state: 'waiting' | 'uploading' | 'held' | 'done' | 'failed' | 'stopped';
  /** Measured from real upload progress, never interpolated. */
  percent: number;
  secondsRemaining: number | null;
  /** How many times this file has been sent, including automatic retries. */
  attempts: number;
  outcome?: ImportOutcome;
  error?: string;
}

/**
 * How many uploads run at once.
 *
 * A thousand dropped documents must NOT become a thousand simultaneous
 * requests: the browser would stall, the server would be swamped, and a failure
 * mid-way would be indistinguishable from a hang. A small window keeps the
 * batch draining at a steady, observable rate whatever its size.
 */
const CONCURRENCY = 3;

/**
 * Automatic attempts before a person is asked to decide.
 *
 * "We never let things hang." A connection that drops once is the common case
 * and the customer should not have to notice it. A connection that drops twice
 * is a fact they need, so the third failure stops and offers the button rather
 * than looping forever behind their back.
 */
const MAX_AUTO_ATTEMPTS = 3;

/** What the folder walk decided not to send, kept so the user is told. */
type Skipped = FolderScan['skipped'];

export function ImportPanel({
  projectId,
  onImported,
}: {
  /**
   * The project an upload is filed against, or `null` while the customer has
   * not named one yet.
   *
   * ── Why nullable (2026-10-04) ────────────────────────────────────────────
   * `projectId` is used in exactly ONE place in this component — the upload URL
   * below. The drop zone, the drag feedback, the preview, the per-file queue and
   * the retry need no project whatsoever. They were nonetheless hidden entirely
   * whenever one was not selected, because `ImportDataTab` wrapped the whole
   * zone in `{active !== null && …}`.
   *
   * That contradicted this file's own rule: *"Dropping a file here says 'I am
   * here, take this' — not 'this is project data'."* If the drop decides
   * nothing, it cannot require the decision first — and the header of
   * `ImportDataTab` says the point was that an agency with no projects yet
   * should create one here "rather than being sent to /projects/new and LOSING
   * THE FILE THEY WERE ABOUT TO DROP".
   *
   * So the zone is always on screen and the file is always accepted. Only the
   * SEND waits, which is signal 3 — the file stays in memory, nobody starts
   * over — applied to the unbound case rather than to a failed request.
   */
  projectId: string | null;
  onImported?: () => void;
}): JSX.Element {
  const inputRef = useRef<HTMLInputElement>(null);
  /** Live only while a batch is running. Aborting it is what "walk away" means. */
  const abortRef = useRef<AbortController | null>(null);

  const [dragging, setDragging] = useState(false);
  const [dragCount, setDragCount] = useState(0);
  const [busy, setBusy] = useState(false);
  const [queue, setQueue] = useState<QueueItem[]>([]);
  const [skipped, setSkipped] = useState<Skipped>([]);
  const [scanning, setScanning] = useState(false);
  /** Set when the folder was larger than one import takes. Never left unsaid. */
  const [walkStopped, setWalkStopped] = useState<'files' | 'entries' | null>(null);

  function patch(index: number, change: Partial<QueueItem>): void {
    setQueue((q) => q.map((item, i) => (i === index ? { ...item, ...change } : item)));
  }

  /**
   * Send one file, reporting real progress as it goes.
   *
   * Retries are AUTOMATIC up to `MAX_AUTO_ATTEMPTS`, then the row keeps its
   * place and its file and waits for a person. Progress resets to 0 on a new
   * attempt because that is the truth — the bytes have to go again. Claiming to
   * resume from 90% when the request restarts would be the same decoration this
   * whole surface exists to remove.
   */
  async function sendOne(item: QueueItem, index: number, signal: AbortSignal): Promise<void> {
    for (let attempt = item.attempts + 1; attempt <= MAX_AUTO_ATTEMPTS; attempt += 1) {
      if (signal.aborted) return;
      patch(index, { state: 'uploading', percent: 0, secondsRemaining: null, attempts: attempt });

      // HELD, not failed. Without a project there is no URL to post to, so the
      // file waits in its lane with its bytes intact and sends the moment one is
      // named. Posting to `/v3/projects/null/imports` would turn a missing
      // choice into a server error the customer cannot act on.
      if (projectId === null) {
        patch(index, { state: 'held', percent: 0, secondsRemaining: null, attempts: attempt });
        return;
      }

      try {
        const form = new FormData();
        form.append('file', item.file);
        const result = await apiUpload<ImportOutcome>(
          `/v3/projects/${projectId}/imports`,
          form,
          {
            signal,
            onProgress: (p) =>
              patch(index, { percent: p.percent, secondsRemaining: p.secondsRemaining }),
          },
        );
        patch(index, { state: 'done', percent: 100, secondsRemaining: null, outcome: result });
        return;
      } catch (err: unknown) {
        if (signal.aborted) {
          patch(index, { state: 'stopped' });
          return;
        }
        const message = err instanceof Error ? err.message : 'Could not be imported.';
        // One failure never blocks the other nine: this row records its own
        // outcome and the pool moves on regardless.
        if (attempt >= MAX_AUTO_ATTEMPTS) {
          patch(index, { state: 'failed', error: message, secondsRemaining: null });
          return;
        }
        // A short pause before trying again — an instant retry usually hits the
        // same broken connection.
        await new Promise((r) => setTimeout(r, 400 * attempt));
      }
    }
  }

  /** Re-send ONE row, from the file we still hold. No dialog, no re-selection. */
  const retryOne = useCallback(
    (index: number) => {
      const item = queue[index];
      if (!item || item.state === 'uploading') return;
      const controller = abortRef.current ?? new AbortController();
      abortRef.current = controller;
      // Attempts reset: this is a decision, not a continuation of the automatic
      // run that already gave up.
      void sendOne({ ...item, attempts: 0 }, index, controller.signal);
    },
    [queue],
  );

  /**
   * A project arrived — release anything held for one.
   *
   * Reuses `retryOne` rather than a second send path: a held file and a failed
   * file both need exactly "send this one again", and two code paths that mean
   * the same thing drift. Guarded on `projectId` being non-null so this does
   * nothing on mount or while still unbound.
   */
  useEffect(() => {
    if (projectId === null) return;
    queue.forEach((item, index) => {
      if (item.state === 'held') retryOne(index);
    });
    // `queue` is intentionally absent from the dependency list: this fires on
    // the project ARRIVING, not on every queue mutation. Including it would
    // re-enter on each patch the send itself makes, and each re-entry would
    // re-send the files already in flight.
    //
    // No eslint-disable here — `react-hooks/exhaustive-deps` is not configured
    // in this project, so a disable comment naming it is itself a lint error
    // ("Definition for rule was not found"). A suppression for a rule that does
    // not run suppresses nothing and fails the gate.
  }, [projectId, retryOne]);

  /** Bounded worker pool — the batch drains at a steady rate, whatever its size. */
  async function uploadAll(
    candidates: readonly File[],
    stoppedReason: 'files' | 'entries' | null = null,
  ): Promise<void> {
    if (busy) return;

    setWalkStopped(stoppedReason);
    if (candidates.length === 0) {
      setQueue([]);
      setSkipped([]);
      return;
    }

    // Split before sending. A folder is mostly not documents, and the things we
    // decline are reported by name rather than dropped on the floor.
    const scan = triage(candidates);
    setSkipped(scan.skipped);
    const files = scan.accepted;
    if (files.length === 0) {
      setQueue([]);
      return;
    }

    const items: QueueItem[] = files.map((f) => ({
      file: f,
      name: f.name,
      size: f.size,
      kind: fileKind(f.name),
      state: 'waiting',
      percent: 0,
      secondsRemaining: null,
      attempts: 0,
    }));

    setBusy(true);
    setQueue(items);

    const controller = new AbortController();
    abortRef.current = controller;

    let next = 0;
    async function worker(): Promise<void> {
      for (;;) {
        // Checked BEFORE claiming the next index, so stopping does not start
        // one more upload after the person asked it to stop.
        if (controller.signal.aborted) return;
        const index = next;
        next += 1;
        if (index >= items.length) return;
        await sendOne(items[index] as QueueItem, index, controller.signal);
      }
    }
    await Promise.all(Array.from({ length: Math.min(CONCURRENCY, items.length) }, () => worker()));

    // Anything still queued when the batch ends was never started.
    setQueue((q) =>
      q.map((item) => (item.state === 'waiting' ? { ...item, state: 'stopped' } : item)),
    );
    abortRef.current = null;
    setBusy(false);
    if (inputRef.current) inputRef.current.value = '';
    onImported?.();
  }

  const onDrop = useCallback(
    (e: React.DragEvent<HTMLElement>) => {
      e.preventDefault();
      setDragging(false);
      setDragCount(0);
      if (busy) return;

      // Captured SYNCHRONOUSLY — the item list is emptied as soon as this
      // handler yields, so an await before this line reads back nothing.
      const entries = entriesFromDrop(e.dataTransfer);
      const flat = Array.from(e.dataTransfer.files ?? []);

      // Dropping a FOLDER puts nothing in `.files`; the tree has to be walked.
      const hasDirectory = entries.some((entry) => entry.isDirectory);
      if (!hasDirectory) {
        if (flat.length > 0) void uploadAll(flat);
        return;
      }

      setScanning(true);
      void filesFromEntries(entries)
        .then((walk) => uploadAll(walk.files, walk.stopped))
        .finally(() => setScanning(false));
    },
    [busy],
  );

  const onDragOver = useCallback((e: React.DragEvent<HTMLElement>) => {
    // Without preventDefault the browser NAVIGATES to the dropped file, losing
    // whatever the user was doing.
    e.preventDefault();
    setDragging(true);
    // How many, not which: a browser deliberately withholds filenames until the
    // drop, so a count is the most the zone can honestly say here.
    setDragCount(Array.from(e.dataTransfer?.items ?? []).filter((i) => i.kind === 'file').length);
  }, []);

  const settled = queue.filter((q) => q.state !== 'waiting' && q.state !== 'uploading').length;
  const failed = queue.filter((q) => q.state === 'failed').length;
  const stopped = queue.filter((q) => q.state === 'stopped').length;
  const read = queue.filter((q) => q.state === 'done').length;

  return (
    <section className="mt-8">
      <h2 className="text-h-s font-semibold">Import documents</h2>
      <p className="mt-1 text-body-s text-warm-gray-d">
        Drop in as many documents as you like — or a whole folder. Kathedral reads each one,
        works out what it is, and puts it where it belongs in this project.
      </p>

      {/*
        SIGNAL 01 — the zone answers back BEFORE the drop, in three ways at once:
        the border changes, a glow comes up behind it, and the copy changes to
        "Release to upload". Users hesitate when nothing moves.

        ONE surface. E3: "we only need one process… Just one drop and drag UI."
        The zone IS the control — a label wrapping a visually-hidden input — so a
        click anywhere opens the picker and a keyboard lands directly on it.
      */}
      <label
        onDrop={onDrop}
        onDragOver={onDragOver}
        onDragLeave={() => {
          setDragging(false);
          setDragCount(0);
        }}
        data-testid="import-dropzone"
        className={
          'relative mt-3 block cursor-pointer overflow-hidden border-2 border-dashed p-8 text-center transition-all duration-150 ' +
          'focus-within:outline focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-trust ' +
          (dragging
            ? 'border-trust bg-paper-blue shadow-[0_0_0_6px_rgba(37,99,235,0.12),0_0_40px_rgba(37,99,235,0.25)]'
            : 'border-rule bg-paper-soft hover:border-trust')
        }
      >
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPT_ATTRIBUTE}
          disabled={busy || scanning}
          multiple
          onChange={(e) => {
            const files = Array.from(e.target.files ?? []);
            if (files.length > 0) void uploadAll(files);
          }}
          className="sr-only"
        />
        <span className="block text-body-l font-medium">
          {dragging ? 'Release to upload' : 'Drop documents or a folder here'}
        </span>
        <span className="mt-2 block text-body-s text-warm-gray-d">
          {dragging
            ? dragCount > 0
              ? `${dragCount} ${dragCount === 1 ? 'item' : 'items'} — Kathedral will read each one`
              : 'Kathedral will read each one'
            : 'or click to choose files · a folder has to be dragged in'}
        </span>
      </label>

      {scanning ? (
        <p className="mt-3 text-body-s" role="status" aria-live="polite">
          Looking through that folder…
        </p>
      ) : null}

      {walkStopped !== null ? (
        <p className="mt-4 text-body-s" role="alert">
          That folder holds more than one import takes, so Kathedral stopped part-way. What you
          see below is what it read — the rest is still waiting. Import the remaining folders
          separately and nothing will be counted twice: a file already read is recognised as the
          same document.
        </p>
      ) : null}

      {/* SIGNAL 05 — every file its own lane, its own progress, its own retry. */}
      {queue.length > 0 ? (
        <div className="mt-6" role="status" aria-live="polite">
          <div className="flex items-baseline justify-between gap-4">
            <p className="text-body-s font-medium">
              {busy ? `Reading ${queue.length} documents` : `${read} of ${queue.length} read.`}
            </p>

            <div className="flex items-center gap-3">
              {failed > 0 ? (
                <span className="border border-danger px-2 py-0.5 text-body-xs text-danger">
                  {settled} of {queue.length} — {failed} failed
                </span>
              ) : null}
              {stopped > 0 && !busy ? (
                <span className="text-body-xs text-warm-gray-d">{stopped} not started</span>
              ) : null}

              {/*
                SIGNAL 02 — wait or walk away. Stopping ABORTS the requests in
                flight; it does not merely hide the bars.
              */}
              {busy ? (
                <button
                  type="button"
                  onClick={() => abortRef.current?.abort()}
                  className="shrink-0 border border-rule px-3 py-1 text-body-xs hover:border-trust"
                >
                  Stop
                </button>
              ) : null}
            </div>
          </div>

          <ul className="mt-3 space-y-2">
            {queue.map((item, i) => (
              <li
                key={`${item.name}-${i}`}
                className={
                  'border p-3 ' +
                  (item.state === 'failed' ? 'border-danger bg-paper-soft' : 'border-rule')
                }
              >
                <div className="flex items-baseline gap-3">
                  {/*
                    SIGNAL 04 — a filename is not feedback. The type and the size
                    are what prove we received the file you meant.

                    NOTE: the reference shows an image thumbnail. Kathedral does
                    not accept images — these are workbooks, documents and PDFs,
                    and there is nothing to render. The stronger proof for a
                    document is what we READ OUT of it, which appears below once
                    it lands: what we think it is, why, and how many rows.
                  */}
                  <span className="shrink-0 border border-rule px-1.5 py-0.5 text-body-xs font-medium text-warm-gray-d">
                    {item.kind}
                  </span>
                  <span className="min-w-0 flex-1 truncate text-body-s font-medium">
                    {item.name}
                  </span>
                  <span className="shrink-0 text-body-xs text-warm-gray-d">
                    {humanSize(item.size)}
                  </span>

                  {item.state === 'uploading' ? (
                    <span className="shrink-0 text-body-s font-semibold text-trust">
                      {item.percent}%
                    </span>
                  ) : null}
                  {item.state === 'done' ? (
                    <span className="shrink-0 text-body-s text-trust" aria-label="Read">
                      ✓
                    </span>
                  ) : null}
                </div>

                {/* SIGNAL 02 — this file's own bar, measured not interpolated. */}
                {item.state === 'uploading' || item.state === 'failed' ? (
                  <div
                    className="mt-2 h-1.5 w-full overflow-hidden bg-rule"
                    role="progressbar"
                    aria-valuenow={item.percent}
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-label={`${item.name} upload progress`}
                  >
                    <div
                      className={
                        'h-full transition-[width] duration-200 ' +
                        (item.state === 'failed' ? 'bg-danger' : 'bg-trust')
                      }
                      style={{ width: `${item.percent}%` }}
                    />
                  </div>
                ) : null}

                {item.state === 'waiting' ? (
                  <span className="mt-1 block text-body-xs text-warm-gray-d">Waiting…</span>
                ) : null}

                {/* HELD — accepted, intact, and waiting on a project rather than
                    on the network. Said in those words because "Waiting…" would
                    leave someone watching a bar that is never going to move, and
                    "Failed" would invite a retry that cannot succeed either. */}
                {item.state === 'held' ? (
                  <span className="mt-1 block text-body-xs text-warm-gray-d">
                    Held — name the project above and this sends itself.
                  </span>
                ) : null}

                {item.state === 'uploading' ? (
                  <span className="mt-1 block text-body-xs text-warm-gray-d">
                    {item.secondsRemaining !== null
                      ? humanTimeLeft(item.secondsRemaining)
                      : 'Measuring…'}
                    {item.attempts > 1 ? ` · attempt ${item.attempts}` : ''}
                  </span>
                ) : null}

                {item.state === 'stopped' ? (
                  // Not a failure. Nothing is wrong with this document.
                  <span className="mt-1 block text-body-xs text-warm-gray-d">
                    Not read — you stopped the import.
                  </span>
                ) : null}

                {/* SIGNAL 03 — the file is still here. One tap sends it again. */}
                {item.state === 'failed' ? (
                  <div className="mt-2 flex items-center justify-between gap-3">
                    <span className="text-body-xs text-danger" role="alert">
                      Paused at {item.percent}% — file kept in memory. {item.error}
                    </span>
                    <button
                      type="button"
                      onClick={() => retryOne(i)}
                      className="shrink-0 border border-rule px-3 py-1 text-body-xs hover:border-trust"
                    >
                      ↻ Retry
                    </button>
                  </div>
                ) : null}

                {item.state === 'done' && item.outcome ? (
                  <span className="mt-1 block text-body-xs text-warm-gray-d">
                    Read as <strong>{humanise(item.outcome.proposed_doc_type)}</strong>
                    {item.outcome.evidence.length > 0
                      ? ` — because it contains ${item.outcome.evidence
                          .slice(0, 2)
                          .map((e) => e.phrase)
                          .filter(Boolean)
                          .join(' · ')}`
                      : ''}
                    {item.outcome.records_written > 0
                      ? ` · ${item.outcome.records_written} rows landed`
                      : ''}
                    {item.outcome.unread_sheets.length > 0
                      ? ` · could not read: ${item.outcome.unread_sheets.join(', ')}`
                      : ''}
                    {item.outcome.truncated ? ' · stopped part-way' : ''}
                  </span>
                ) : null}

                {/*
                  SIGNAL 04 — the file is saved and NOTHING inside it was read.
                  Until the receipt existed this rendered as "Read as Strategic
                  plan" and nothing more, because the rows line is suppressed at
                  zero — a document whose contents never landed, looking exactly
                  like a clean import. `warning`, not `danger`: nothing failed,
                  the file is kept and retrievable. But the customer uploaded it
                  to be understood, and it was not.
                */}
                {item.state === 'done' && item.outcome?.outcome === 'nothing_landed' ? (
                  <span className="mt-1 block text-body-xs text-warning" role="status">
                    Saved — but nothing inside it was read.
                    {item.outcome.outcome_reason ? ` ${item.outcome.outcome_reason}` : ''}
                  </span>
                ) : null}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {skipped.length > 0 ? (
        // Named, never counted away. A folder import that quietly declines 25
        // files reads as "everything landed" — which is how a CEO ends up
        // briefing a Board from a picture with a hole in it.
        <div className="mt-4 border border-rule bg-paper-soft p-3">
          <p className="text-body-s font-medium">
            {skipped.length} {skipped.length === 1 ? 'file was' : 'files were'} not imported
          </p>
          <ul className="mt-2">
            {skipped.map((item) => (
              <li key={item.name} className="text-body-xs text-warm-gray-d">
                <span className="font-medium">{item.name}</span> — {item.reason}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </section>
  );
}
